import {
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { app, auth } from './client';
import {
  isMainPage,
  uiDisplayErrorMessage,
  uiGetAuthContainer,
  uiResetLoginError,
  uiSetAuthTemplate,
  uiSetLoginBusy
} from './authUI';
import { firebaseConfig } from './firebaseConfig';
import {
  disableGoogleAutoSelectOnNextPrompt,
  isStandaloneDisplayMode,
  promptGoogleOneTap,
  renderGoogleSignInButton
} from './googleSignIn';
import { SessionManager } from './sessionManager';
import { redirectingToCanonicalHost } from './canonicalHost';

export const USER_PROFILE_UPDATED_EVENT = 'jidoka:user-profile-updated';
export const AUTH_STATE_CHANGED_EVENT = 'jidoka:auth-state-changed';
export const USER_CACHE_SCHEMA_VERSION = 2;
const USER_KEY = 'USER_KEY';
const AVATAR_CACHE_KEY = 'JIDOKA_USER_AVATAR_CACHE';
const AUTH_REDIRECT_PENDING_KEY = 'JIDOKA_AUTH_REDIRECT_PENDING';
const AUTH_RETURN_TARGET_KEY = 'JIDOKA_AUTH_RETURN_TARGET';
const AUTH_REDIRECT_QUERY = 'authRedirect';
const AUTH_SWITCH_ACCOUNT_QUERY = 'switchAccount';
const AUTH_SWITCH_ERROR_QUERY = 'switchError';
const AUTH_SIGNED_OUT_QUERY = 'signedOut';
const PUSH_UNREGISTER_TIMEOUT_MS = 3000;
const SIGN_IN_RETRY_DELAY_MS = 1500;

type SignedOutReason = 'inactive' | 'denied';

export type AuthLifecycleState = 'initializing' | 'signed-out' | 'signing-in' | 'hydrating-profile' | 'authenticated' | 'denied' | 'error';

type LoginNotice =
  | { kind: 'status'; message: string }
  | { kind: 'error'; message: string };

export interface CachedUser {
  displayName: string;
  email: string;
  photoURL: string;
  uid: string;
  inactive: boolean;
  cachedAt: number;
  schemaVersion: number;
  permissions: {
    apiAccess: boolean;
    shipmentCreate: boolean;
    shipmentDelete: boolean;
    shipmentEdit: boolean;
    adminAccess: boolean;
  };
  preferences?: { theme?: 'SYSTEM' | 'LIGHT' | 'DARK'; defaultListLength?: number };
  slack?: { id: string; real_name: string; display_name: string; avatar: string; color: string };
}

export type AuthReadyResult =
  | { state: 'authenticated'; user: FirebaseUser; profile: CachedUser }
  | { state: 'signed-out' | 'denied' | 'error'; user: null; profile: null };

let lifecycleState: AuthLifecycleState = 'initializing';
let sessionManager: SessionManager | null = null;
let activeProfileRefresh: Promise<CachedUser> | null = null;
let monitorStarted = false;
let authTransitionInProgress = false;
let googleIdSignInInProgress = false;
let standaloneLoginPreparationInProgress = false;
let standaloneLoginPreparationFailed = false;
let authInitializationPromise: Promise<void> | null = null;
let resolveAuthReady: (result: AuthReadyResult) => void;
let authReadySettled = false;
let authReadyPromise = new Promise<AuthReadyResult>(resolve => { resolveAuthReady = resolve; });

const setLifecycleState = (state: AuthLifecycleState): void => {
  lifecycleState = state;
  window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGED_EVENT, { detail: { state } }));
};

const settleAuthReady = (result: AuthReadyResult): void => {
  if (!authReadySettled) {
    authReadySettled = true;
    resolveAuthReady(result);
  }
};

export const whenAuthReady = (): Promise<AuthReadyResult> => authReadyPromise;
export const getAuthLifecycleState = (): AuthLifecycleState => lifecycleState;

const normalizeEmail = (email: string | null | undefined): string => (email || '').trim().toLowerCase();
const isAllowedEmail = (email: string | null | undefined): boolean => /^[^@\s]+@voltera\.io$/i.test((email || '').trim());

const readSessionValue = (key: string): string | null => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};

const writeSessionValue = (key: string, value: string): void => {
  try { sessionStorage.setItem(key, value); } catch { /* Redirect still works without optional recovery metadata. */ }
};

const removeSessionValue = (key: string): void => {
  try { sessionStorage.removeItem(key); } catch { /* Nothing to clear when storage is unavailable. */ }
};

const safeReturnTarget = (): string => {
  const raw = new URLSearchParams(window.location.search).get('to')
    || readSessionValue(AUTH_RETURN_TARGET_KEY)
    || '/ncr';
  try {
    const candidate = new URL(raw.startsWith('/') ? raw : `/${raw}`, window.location.origin);
    return candidate.origin === window.location.origin ? `${candidate.pathname}${candidate.search}${candidate.hash}` : '/ncr';
  } catch {
    return '/ncr';
  }
};

const rememberReturnTarget = (): void => {
  writeSessionValue(AUTH_RETURN_TARGET_KEY, safeReturnTarget());
};

const markRedirectPending = (): void => {
  writeSessionValue(AUTH_REDIRECT_PENDING_KEY, '1');
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(AUTH_REDIRECT_QUERY, '1');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch { /* Session storage remains the recovery marker. */ }
};

const clearRedirectState = (): void => {
  removeSessionValue(AUTH_REDIRECT_PENDING_KEY);
  removeSessionValue(AUTH_RETURN_TARGET_KEY);
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(AUTH_REDIRECT_QUERY)) {
      url.searchParams.delete(AUTH_REDIRECT_QUERY);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch { /* Navigation will discard the marker after successful sign-in. */ }
};

const consumeLoginNotice = (): LoginNotice | null => {
  if (!isMainPage) return null;

  try {
    const url = new URL(window.location.href);
    const switchFailed = url.searchParams.get(AUTH_SWITCH_ERROR_QUERY) === '1';
    const switchingAccount = url.searchParams.get(AUTH_SWITCH_ACCOUNT_QUERY) === '1';
    const signedOutReason = url.searchParams.get(AUTH_SIGNED_OUT_QUERY);
    if (!switchFailed && !switchingAccount && !signedOutReason) return null;

    url.searchParams.delete(AUTH_SWITCH_ERROR_QUERY);
    url.searchParams.delete(AUTH_SWITCH_ACCOUNT_QUERY);
    url.searchParams.delete(AUTH_SIGNED_OUT_QUERY);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

    if (signedOutReason === 'inactive') {
      return { kind: 'status', message: 'You were signed out after 7 days of inactivity. Sign in to continue.' };
    }
    if (signedOutReason === 'denied') {
      return { kind: 'error', message: 'Your Jidoka access could not be confirmed. Contact an administrator if this continues.' };
    }
    return switchFailed
      ? { kind: 'error', message: 'We signed you out, but could not open the account chooser. Please try again.' }
      : { kind: 'status', message: 'Choose the Google account you want to use.' };
  } catch {
    return null;
  }
};

const showLoginNotice = (notice: LoginNotice | null): void => {
  if (!notice) return;
  uiSetLoginBusy(false, notice.kind === 'status' ? notice.message : '');
  if (notice.kind === 'error') uiDisplayErrorMessage(notice.message);
};

const currentPath = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;
const redirectToLogin = (): void => {
  const loginUrl = new URL('/', window.location.origin);
  if (!isMainPage) loginUrl.searchParams.set('to', currentPath());
  window.location.assign(loginUrl.toString());
};

const clearLocalAuthState = (clearSession = true): void => {
  sessionManager?.stop(clearSession);
  sessionManager = null;
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(AVATAR_CACHE_KEY);
  // clearSession marks an explicit sign-out (logout, switch, timeout, denial).
  if (clearSession) disableGoogleAutoSelectOnNextPrompt();
};

const signOutEveryAuthRuntime = async (): Promise<void> => {
  try {
    // Device cleanup is best-effort; a slow network must not stall sign-out.
    const { disableNotificationPush } = await import('../notifications/PushNotifications');
    await Promise.race([
      disableNotificationPush(),
      new Promise<void>(resolve => window.setTimeout(resolve, PUSH_UNREGISTER_TIMEOUT_MS))
    ]);
  } catch (error) {
    console.warn('[Auth] Unable to unregister notification device before sign-out:', error);
  }
  await signOut(auth);
};

const signOutWithNotice = async (reason: SignedOutReason): Promise<void> => {
  authTransitionInProgress = true;
  clearLocalAuthState(true);
  const loginUrl = new URL('/', window.location.origin);
  loginUrl.searchParams.set(AUTH_SIGNED_OUT_QUERY, reason);
  const returnTo = isMainPage ? new URLSearchParams(window.location.search).get('to') : currentPath();
  if (returnTo) loginUrl.searchParams.set('to', returnTo);
  try {
    await signOutEveryAuthRuntime();
  } finally {
    window.location.replace(loginUrl.toString());
  }
};

const permissionsFallback = (): CachedUser['permissions'] => ({
  apiAccess: false, shipmentCreate: false, shipmentDelete: false, shipmentEdit: false, adminAccess: false
});

const toCachedUser = (data: Partial<CachedUser>): CachedUser => ({
  displayName: data.displayName || 'User',
  email: normalizeEmail(data.email),
  photoURL: data.photoURL || '/assets/img/avatar.png',
  uid: data.uid || '',
  inactive: data.inactive === true,
  cachedAt: Date.now(),
  schemaVersion: USER_CACHE_SCHEMA_VERSION,
  permissions: { ...permissionsFallback(), ...(data.permissions || {}) },
  preferences: data.preferences || { theme: 'SYSTEM' },
  slack: data.slack
});

const errorText = (error: unknown): string =>
  `${errorCode(error)} ${error instanceof Error ? error.message : ''}`;

// The userLogin blocking function rejects non-Voltera accounts with this text.
const isBlockedAccountError = (error: unknown): boolean =>
  errorCode(error).includes('internal-error') && /Unauthorized email/i.test(errorText(error));

// Blocking functions scale to zero; a cold start can exceed Firebase Auth's
// 7-second limit and surface as internal-error. A retry reaches a warm instance.
const isSignInStartupError = (error: unknown): boolean =>
  errorCode(error).includes('internal-error') && !isBlockedAccountError(error);

const authErrorMessage = (error: unknown): string => {
  const code = errorCode(error);
  if (isBlockedAccountError(error)) return 'Use your @voltera.io Google account to sign in.';
  if (isSignInStartupError(error)) return 'Jidoka took too long to finish signing you in. Please try again; the next attempt is usually instant.';
  if (code.includes('popup-closed-by-user')) return 'Google sign-in was cancelled. Please try again.';
  if (code.includes('popup-blocked')) return 'Your browser blocked the sign-in window. Trying another Google sign-in method…';
  if (code.includes('network-request-failed') || code.includes('unavailable')) return 'Sign-in could not reach Google. Check your connection and try again.';
  if (code.includes('unauthorized-domain')) return 'This site is not authorized for Google sign-in. Please contact support.';
  if (code.includes('permission-denied')) return 'This account is not authorized to use Jidoka.';
  if (code.includes('google-sign-in-failed') || code.includes('operation-not-supported')) {
    return 'Google sign-in is unavailable right now. Try again. If the problem continues, open Jidoka in Safari or Chrome.';
  }
  return 'We could not sign you in. Please try again.';
};

const isPopupFallbackError = (error: unknown): boolean => {
  const code = typeof error === 'object' && error && 'code' in error ? String((error as { code: unknown }).code) : '';
  return code.includes('popup-blocked')
    || code.includes('operation-not-supported-in-this-environment');
};

const signInWithGoogle = async (): Promise<void> => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: 'voltera.io', prompt: 'select_account' });

  // Local Auth Emulator: keep Google Identity Services / production OAuth out of
  // the loop. Prefer the emulator popup, and fall back to a synthetic credential
  // when popups are unavailable (Cursor preview, headless, etc.).
  if (isLocalDevelopmentHost()) {
    try {
      await signInWithPopup(auth, provider);
      return;
    } catch (error) {
      if (isPopupFallbackError(error) || isCursorIntegratedBrowser()) {
        uiSetLoginBusy(true, 'Signing into the local development account…');
        await signInWithCredential(auth, getCursorDevelopmentCredential());
        return;
      }
      throw error;
    }
  }

  // Installed PWAs must avoid Firebase redirect/popup auth-helper flows. Their
  // login page renders the Google Identity Services ID-token button instead.
  if (isStandaloneDisplayMode()) {
    throw Object.assign(
      new Error('Standalone login requires the Google sign-in control'),
      { code: 'auth/operation-not-supported-in-this-environment' }
    );
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    if (!isPopupFallbackError(error)) throw error;

    uiSetLoginBusy(true, 'Continuing to Google…');
    rememberReturnTarget();
    markRedirectPending();
    await signInWithRedirect(auth, provider);
  }
};

const isLocalDevelopmentHost = (): boolean =>
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

const isCursorIntegratedBrowser = (): boolean =>
  isLocalDevelopmentHost() && /\bCursor\/[\d.]+/i.test(navigator.userAgent);

const getCursorDevelopmentCredential = () => {
  const configuredEmail = localStorage.getItem('JIDOKA_DEV_AUTH_EMAIL')?.trim().toLowerCase();
  const email = configuredEmail && isAllowedEmail(configuredEmail)
    ? configuredEmail
    : 'developer@voltera.io';
  const token = JSON.stringify({
    sub: `cursor-${email}`,
    email,
    email_verified: true,
    name: 'Cursor Developer'
  });

  return GoogleAuthProvider.credential(token);
};

const handleLoginFailure = (error: unknown): void => {
  clearRedirectState();
  setLifecycleState('error');
  uiSetLoginBusy(false);
  uiDisplayErrorMessage(authErrorMessage(error));
};

/**
 * Installed PWAs use the Google Identity Services authentication flow. Google
 * returns an ID token, which Firebase exchanges for the same Firebase Auth
 * session used by every other page and sign-in path.
 */
const logInWithGoogleIdToken = async (idToken: string): Promise<void> => {
  if (googleIdSignInInProgress) return;
  googleIdSignInInProgress = true;
  uiResetLoginError();
  uiSetLoginBusy(true, 'Completing secure Google sign-in…');
  setLifecycleState('signing-in');

  try {
    await authInitializationPromise;
    const previousUid = auth.currentUser?.uid;
    const exchangeIdToken = () => signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
    let credential;
    try {
      credential = await exchangeIdToken();
    } catch (error) {
      if (!isSignInStartupError(error)) throw error;
      // The Google ID token stays valid, so retry once without user action.
      uiSetLoginBusy(true, 'Still signing you in…');
      await new Promise(resolve => window.setTimeout(resolve, SIGN_IN_RETRY_DELAY_MS));
      credential = await exchangeIdToken();
    }
    // Same-account sign-in does not fire onAuthStateChanged; retry the profile
    // explicitly when an earlier profile load failed.
    if (previousUid && previousUid === credential.user.uid) await handleAuthenticatedUser(credential.user);
  } catch (error) {
    handleLoginFailure(error);
  } finally {
    googleIdSignInInProgress = false;
  }
};

const configureStandaloneGoogleLogin = async (): Promise<void> => {
  const container = uiGetAuthContainer();
  if (!container) {
    throw Object.assign(
      new Error('Google sign-in container is unavailable'),
      { code: 'auth/operation-not-supported-in-this-environment' }
    );
  }

  uiSetLoginBusy(true, 'Preparing secure Google sign-in…');
  await renderGoogleSignInButton(container, {
    onCredential: logInWithGoogleIdToken,
    onError: handleLoginFailure
  });
  uiSetLoginBusy(false);
};

function renderStandaloneRetryControl(): void {
  const loginButton = uiSetAuthTemplate();
  loginButton?.addEventListener('click', () => {
    void prepareStandaloneGoogleLogin(null, true);
  });
}

async function prepareStandaloneGoogleLogin(
  notice: LoginNotice | null,
  settleSignedOut = false
): Promise<void> {
  if (standaloneLoginPreparationInProgress) return;
  standaloneLoginPreparationInProgress = true;
  standaloneLoginPreparationFailed = false;
  uiResetLoginError();

  try {
    await configureStandaloneGoogleLogin();
    standaloneLoginPreparationInProgress = false;
    if (settleSignedOut && !auth.currentUser) setLifecycleState('signed-out');
    showLoginNotice(notice);
    // Only offer One Tap once Firebase confirms nobody is signed in, so an
    // existing session is never re-authenticated in the background.
    void whenAuthReady().then(result => {
      if (result.state === 'signed-out' && !auth.currentUser) promptGoogleOneTap();
    });
  } catch (error) {
    standaloneLoginPreparationInProgress = false;
    standaloneLoginPreparationFailed = true;
    // Rendering can fail before or after GIS replaces the temporary button.
    // Always restore a live control so an offline or timed-out PWA can retry.
    renderStandaloneRetryControl();
    handleLoginFailure(error);
  }
}

const loadProfileFromCallable = async (): Promise<Partial<CachedUser>> => {
  const [{ httpsCallable }, { functions }] = await Promise.all([
    import('firebase/functions'),
    import('./functionsClient')
  ]);
  const callable = httpsCallable(functions, 'getCurrentUserProfile');
  const result = await callable();
  return result.data as Partial<CachedUser>;
};

type FirestoreRestValue = {
  nullValue?: null;
  booleanValue?: boolean;
  integerValue?: string;
  doubleValue?: number;
  timestampValue?: string;
  stringValue?: string;
  mapValue?: { fields?: Record<string, FirestoreRestValue> };
  arrayValue?: { values?: FirestoreRestValue[] };
};

const decodeFirestoreValue = (value: FirestoreRestValue): unknown => {
  if ('nullValue' in value) return null;
  if ('booleanValue' in value) return value.booleanValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return value.doubleValue;
  if ('timestampValue' in value) return value.timestampValue;
  if ('stringValue' in value) return value.stringValue;
  if (value.arrayValue) return (value.arrayValue.values || []).map(decodeFirestoreValue);
  if (value.mapValue) return decodeFirestoreFields(value.mapValue.fields || {});
  return undefined;
};

const decodeFirestoreFields = (fields: Record<string, FirestoreRestValue>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, decodeFirestoreValue(value)]));

const loadProfile = async (user: FirebaseUser): Promise<Partial<CachedUser>> => {
  try {
    const token = await user.getIdToken();
    // Local sign-ins carry emulator-signed tokens that production Firestore
    // rejects, so the REST lookup must target the emulator (port must match
    // firebase.json) for the fast path to work in development.
    const firestoreOrigin = isLocalDevelopmentHost()
      ? `http://${window.location.hostname}:8080`
      : 'https://firestore.googleapis.com';
    const endpoint = `${firestoreOrigin}/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents/users/${encodeURIComponent(user.uid)}`;
    const response = await fetch(endpoint, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`profile-read-${response.status}`);
    const document = await response.json() as { fields?: Record<string, FirestoreRestValue> };
    const profile = decodeFirestoreFields(document.fields || {}) as Partial<CachedUser>;
    return {
      ...profile,
      uid: user.uid,
      email: user.email || profile.email,
      photoURL: user.photoURL || profile.photoURL
    };
  } catch (error) {
    // Legacy email-keyed profiles and newly provisioned accounts still need
    // the callable's migration/provisioning path. Normal UID profiles avoid
    // a Cloud Functions cold start during sign-in.
    console.warn('[Auth] Direct profile lookup unavailable; using compatibility fallback.', error);
    return loadProfileFromCallable();
  }
};

const hydrateProfile = async (user = auth.currentUser): Promise<CachedUser> => {
  if (!user) throw Object.assign(new Error('unauthenticated'), { code: 'auth/unauthenticated' });
  if (activeProfileRefresh) return activeProfileRefresh;
  activeProfileRefresh = (async () => {
    const profile = toCachedUser(await loadProfile(user));
    if (!profile.uid || !isAllowedEmail(profile.email) || profile.inactive) {
      const reason = profile.inactive ? 'inactive' : 'unauthorized';
      throw Object.assign(new Error(reason), { code: `auth/${reason}` });
    }
    localStorage.setItem(USER_KEY, JSON.stringify(profile));
    window.dispatchEvent(new CustomEvent<CachedUser>(USER_PROFILE_UPDATED_EVENT, { detail: profile }));
    return profile;
  })();
  try { return await activeProfileRefresh; } finally { activeProfileRefresh = null; }
};

export const refreshCurrentUserProfile = (): Promise<CachedUser> => hydrateProfile();

const errorCode = (error: unknown): string =>
  typeof error === 'object' && error && 'code' in error ? String((error as { code: unknown }).code) : '';

// Only a definitive account answer may end a successful Google sign-in.
// Network faults, Cloud Functions cold starts, and server errors are retried.
const isProfileAccessDenied = (error: unknown): boolean =>
  /inactive|unauthorized|permission-denied|unauthenticated/.test(errorCode(error));

const startAuthenticatedSession = (user: FirebaseUser, profile: CachedUser): boolean => {
  sessionManager?.stop(false);
  const signedInAt = Date.parse(user.metadata.lastSignInTime || '') || 0;
  sessionManager = new SessionManager(user.uid, () => signOutWithNotice('inactive'), signedInAt);
  if (!sessionManager.start()) return false;
  setLifecycleState('authenticated');
  settleAuthReady({ state: 'authenticated', user, profile });
  if (isMainPage) {
    const target = safeReturnTarget();
    clearRedirectState();
    window.location.replace(target);
  }
  return true;
};

const endSessionForDeniedProfile = async (error: unknown): Promise<void> => {
  console.error('[Auth] Profile access denied:', error);
  setLifecycleState('denied');
  settleAuthReady({ state: 'denied', user: null, profile: null });
  if (!isMainPage) {
    await signOutWithNotice('denied');
    return;
  }
  const message = error instanceof Error ? error.message : '';
  const inactive = /inactive/i.test(`${errorCode(error)} ${message}`);
  clearLocalAuthState();
  await signOutEveryAuthRuntime();
  uiSetLoginBusy(false);
  uiDisplayErrorMessage(inactive
    ? 'Your Jidoka account is inactive. Contact an administrator for access.'
    : 'This account is not authorized to use Jidoka.');
};

const handleAuthenticatedUser = async (user: FirebaseUser): Promise<void> => {
  if (!isAllowedEmail(user.email)) {
    setLifecycleState('denied');
    clearLocalAuthState();
    await signOutEveryAuthRuntime();
    uiDisplayErrorMessage('Use your @voltera.io Google account to sign in.');
    settleAuthReady({ state: 'denied', user: null, profile: null });
    return;
  }

  const cached = getCurrentUser();
  const cachedProfile = cached?.uid === user.uid ? cached : null;

  // Returning users start from the cached profile and revalidate in the
  // background, so page loads do not wait on a profile network round-trip.
  if (cachedProfile) {
    if (!startAuthenticatedSession(user, cachedProfile) || isMainPage) return;
    hydrateProfile(user).catch(error => {
      if (isProfileAccessDenied(error)) {
        void endSessionForDeniedProfile(error);
        return;
      }
      console.warn('[Auth] Background profile refresh failed; continuing with cached profile.', error);
    });
    return;
  }

  setLifecycleState('hydrating-profile');
  if (isMainPage) uiSetLoginBusy(true, 'Loading your Jidoka profile…');
  try {
    const profile = await hydrateProfile(user);
    startAuthenticatedSession(user, profile);
  } catch (error) {
    if (isProfileAccessDenied(error)) {
      await endSessionForDeniedProfile(error);
      return;
    }

    // Keep the Firebase session: the user did sign in, and the login page
    // retries profile loading on reload or when the button is pressed again.
    console.error('[Auth] Profile hydration failed:', error);
    setLifecycleState('error');
    settleAuthReady({ state: 'error', user: null, profile: null });
    if (!isMainPage) {
      redirectToLogin();
      return;
    }
    uiSetLoginBusy(false);
    uiDisplayErrorMessage('You are signed in, but Jidoka could not load your profile. Check your connection and try again.');
  }
};

export const monitorAuthState = (): Promise<AuthReadyResult> => {
  if (monitorStarted) return authReadyPromise;
  monitorStarted = true;
  // Leaving for jidoka.ca: never settle, so no page or login UI starts here.
  if (redirectingToCanonicalHost) return authReadyPromise;
  const standaloneGoogleLogin = isMainPage
    && !isLocalDevelopmentHost()
    && isStandaloneDisplayMode();
  const loginNotice = consumeLoginNotice();

  if (isMainPage) {
    if (standaloneGoogleLogin) {
      renderStandaloneRetryControl();
      void prepareStandaloneGoogleLogin(loginNotice);
    } else {
      const loginButton = uiSetAuthTemplate();
      loginButton?.addEventListener('click', () => void logIn());
    }
  }

  // A prior version could leave a redirect marker behind after a failed PWA
  // round-trip. Standalone mode never consumes Firebase redirect state.
  if (standaloneGoogleLogin) clearRedirectState();
  const redirectPending = !standaloneGoogleLogin && (
    readSessionValue(AUTH_REDIRECT_PENDING_KEY) === '1'
      || new URLSearchParams(window.location.search).get(AUTH_REDIRECT_QUERY) === '1'
  );
  let redirectRecoveryFailed = false;
  if (redirectPending) uiSetLoginBusy(true, 'Completing Google sign-in…');
  else if (isMainPage && !standaloneGoogleLogin) uiSetLoginBusy(true, 'Checking your session…');

  authInitializationPromise = (async () => {
    if (redirectPending) {
      try {
        await getRedirectResult(auth);
      } catch (error) {
        redirectRecoveryFailed = true;
        clearRedirectState();
        uiSetLoginBusy(false);
        uiDisplayErrorMessage(authErrorMessage(error));
      }
    }

    onAuthStateChanged(auth, user => {
      if (user) {
        void handleAuthenticatedUser(user);
        return;
      }
      clearLocalAuthState(false);
      setLifecycleState('signed-out');
      settleAuthReady({ state: 'signed-out', user: null, profile: null });
      uiSetLoginBusy(
        standaloneLoginPreparationInProgress,
        standaloneLoginPreparationInProgress ? 'Preparing secure Google sign-in…' : ''
      );
      if (redirectPending && !redirectRecoveryFailed) {
        clearRedirectState();
        uiDisplayErrorMessage('Google sign-in did not complete. Please try again.');
      }
      if (!redirectPending && !standaloneLoginPreparationFailed) showLoginNotice(loginNotice);
      if (!isMainPage && !authTransitionInProgress) redirectToLogin();
    });
  })();
  return authReadyPromise;
};

export const logIn = async (): Promise<void> => {
  // Signed in with Google but the profile failed to load: retry the profile
  // instead of reopening Google.
  if (auth.currentUser && lifecycleState === 'error') {
    uiResetLoginError();
    await handleAuthenticatedUser(auth.currentUser);
    return;
  }

  if (!isLocalDevelopmentHost() && isStandaloneDisplayMode()) {
    await prepareStandaloneGoogleLogin(null, true);
    return;
  }

  uiResetLoginError();
  uiSetLoginBusy(true, 'Opening Google sign-in…');
  setLifecycleState('signing-in');
  try {
    await authInitializationPromise;

    if (isCursorIntegratedBrowser()) {
      uiSetLoginBusy(true, 'Signing into the local development account…');
      await signInWithCredential(auth, getCursorDevelopmentCredential());
      return;
    }

    await signInWithGoogle();
  } catch (error) {
    handleLoginFailure(error);
  }
};

export const logOut = async (): Promise<void> => {
  authTransitionInProgress = true;
  clearLocalAuthState(true);
  try {
    await signOutEveryAuthRuntime();
  } finally {
    window.location.replace('/');
  }
};

export const switchAccount = async (): Promise<void> => {
  authTransitionInProgress = true;
  const returnTo = currentPath();
  clearLocalAuthState(true);
  try {
    await signOutEveryAuthRuntime();

    // Account selection in an installed PWA belongs on the login page, where
    // the GIS ID-token button can render without Firebase redirect state.
    if (!isLocalDevelopmentHost() && isStandaloneDisplayMode()) {
      const loginUrl = new URL('/', window.location.origin);
      loginUrl.searchParams.set('to', returnTo);
      loginUrl.searchParams.set('switchAccount', '1');
      window.location.replace(loginUrl.toString());
      return;
    }

    await signInWithGoogle();
    // Route through the login page so profile hydration and legacy USER_KEY
    // consumers re-initialize for the new account before the page loads.
    const loginUrl = new URL('/', window.location.origin);
    loginUrl.searchParams.set('to', returnTo);
    window.location.replace(loginUrl.toString());
  } catch (error) {
    console.error('[Auth] Account switch failed:', error);
    authTransitionInProgress = false;
    const loginUrl = new URL('/', window.location.origin);
    loginUrl.searchParams.set('to', returnTo);
    loginUrl.searchParams.set('switchError', '1');
    window.location.replace(loginUrl.toString());
  }
};

export const getCurrentUser = (): CachedUser | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedUser>;
    if (!parsed.uid || !isAllowedEmail(parsed.email) || parsed.inactive || parsed.schemaVersion !== USER_CACHE_SCHEMA_VERSION) {
      localStorage.removeItem(USER_KEY);
      return null;
    }
    return parsed as CachedUser;
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
};

export type UserPreferencesPatch = Partial<NonNullable<CachedUser['preferences']>>;

/**
 * Save the active user's supported preferences through the authenticated
 * server path. The callable resolves legacy email-keyed profiles and never
 * permits permission or account-state changes from the browser.
 */
export const updateCurrentUserPreferences = async (patch: UserPreferencesPatch): Promise<CachedUser> => {
  const currentUser = getCurrentUser();
  if (!currentUser || !auth.currentUser || auth.currentUser.uid !== currentUser.uid) {
    throw Object.assign(new Error('You must be signed in to update preferences.'), { code: 'auth/unauthenticated' });
  }

  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./functionsClient');
  const callable = httpsCallable<UserPreferencesPatch, { preferences: UserPreferencesPatch }>(
    functions,
    'updateCurrentUserPreferences'
  );
  const result = await callable(patch);
  const updatedUser = toCachedUser({
    ...currentUser,
    preferences: {
      ...currentUser.preferences,
      ...result.data.preferences
    }
  });

  localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  window.dispatchEvent(new CustomEvent<CachedUser>(USER_PROFILE_UPDATED_EVENT, { detail: updatedUser }));
  return updatedUser;
};

export { app, auth };
export type { FirebaseUser };
